import Axios, { AxiosRequestConfig, AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { useAuthStore } from '@store/useAuthStore';

// 에러 응답 타입
export type ErrorResponse = {
    message?: string;
    statusCode?: number;
}

// API 기본 URL 설정
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:7070';

// Axios 인스턴스 생성
export const AXIOS_INSTANCE = Axios.create({
    baseURL: API_BASE_URL,
    timeout: 60000, // 파일 업로드를 위해 60초로 증가
    headers: {
        'Content-Type': 'application/json'
    },
    withCredentials: true
});

// 토큰 갱신 중복 요청 방지
let isRefreshing = false;
let failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: AxiosError) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
    failedQueue.forEach(promise => {
        if (error) {
            promise.reject(error);
        } else {
            promise.resolve(token!);
        }
    });
    failedQueue = [];
};

// 토큰 갱신 함수
export const refreshAccessToken = async (): Promise<string> => {
	const response = await Axios.post(`${API_BASE_URL}/v1/auth/refresh`, {}, { withCredentials: true });
    const { accessToken } = response.data;
    useAuthStore.getState().setAccessToken(accessToken);
    return accessToken;
};

// Request Interceptor
AXIOS_INSTANCE.interceptors.request.use(
    config => {
        const { accessToken } = useAuthStore.getState();

        if (accessToken && config.headers) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }

        // FormData 를 전송하는 경우 Content-Type 을 삭제
        if (config.data instanceof FormData && config.headers) {
            delete config.headers['Content-Type'];
        }

        // 엑셀 다운로드 엔드포인트는 responseType을 blob으로 설정
        if (config.url?.includes('/excel')) {
            config.responseType = 'blob';
        }

        return config;
    },
    error => Promise.reject(error)
);

// 토큰 갱신이 필요 없는 API 경로
const AUTH_WHITELIST = ['/admins/login', '/admins/register', '/admins/check-email', '/auth/refresh'];

// 파일 다운로드 헬퍼 함수
const downloadFile = (blob: Blob, contentDisposition?: string) => {
    let fileName = 'download.xlsx';

    if (contentDisposition) {
        // RFC 5987 형식 파싱 (filename*=UTF-8''encoded-name)
        const rfc5987Match = contentDisposition.match(/filename\*=UTF-8''([^;\n]*)/i);
        if (rfc5987Match && rfc5987Match[1]) {
            fileName = decodeURIComponent(rfc5987Match[1]);
        } else {
            // 일반 형식 파싱 (filename="name" 또는 filename=name)
            const standardMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (standardMatch && standardMatch[1]) {
                fileName = standardMatch[1].replace(/['"]/g, '');
                fileName = decodeURIComponent(fileName);
            }
        }
    }

    // Blob URL 생성 및 다운로드
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

// Response Interceptor
AXIOS_INSTANCE.interceptors.response.use(response => {
        // Blob 응답인 경우 자동 다운로드 처리
        if (response.data instanceof Blob) {
            downloadFile(response.data, response.headers['content-disposition']);
            toast.success('파일 다운로드가 완료되었습니다.');
        }
        return response;
    },
    async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
        const requestUrl = originalRequest.url || '';

        // 인증 불필요 API는 401 처리 스킵
        const isWhitelisted = AUTH_WHITELIST.some(path => requestUrl.includes(path));

        // 401 에러 & 재시도하지 않은 요청 & whitelist 아닌 경우
        if (error.response?.status === 401 && !originalRequest._retry && !isWhitelisted) {
            // 이미 토큰 갱신 중이면 대기열에 추가
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({
                        resolve: (token: string) => {
                            if (originalRequest.headers) {
                                originalRequest.headers.Authorization = `Bearer ${token}`;
                            }
                            resolve(AXIOS_INSTANCE(originalRequest));
                        },
                        reject: (err: AxiosError) => {
                            reject(err);
                        }
                    });
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // 토큰 갱신 요청
                const accessToken = await refreshAccessToken();
                processQueue(null, accessToken);

                if (originalRequest.headers) {
                    originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                }
                return AXIOS_INSTANCE(originalRequest);
            } catch (refreshError) {
                // 토큰 갱신 실패 시 로그아웃 처리 및 로그인 페이지로 리다이렉트
                processQueue(refreshError as AxiosError, null);
                useAuthStore.getState().logout();

                if (typeof window !== 'undefined') {
                    window.location.href = '/login';
                }

                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        // 에러 메시지 처리
        const errorData = error.response?.data as ErrorResponse;
        const errorMessage = errorData?.message || '요청 처리 중 오류가 발생했습니다.';
        toast.error(errorMessage);

        return Promise.reject(error);
    }
);

// Orval custom instance
export const axiosInstance = <T>(config: AxiosRequestConfig): Promise<T> => {
    const source = Axios.CancelToken.source();
    const promise = AXIOS_INSTANCE({
        ...config,
        cancelToken: source.token
    }).then(({ data }) => data);

    // @ts-ignore
    promise.cancel = () => {
        source.cancel('Query was cancelled');
    };

    return promise;
};
